import { ChangeEvent, FC, useEffect, useMemo } from "react";
import {
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps,
  Stack,
  StackProps,
} from "@mui/material";
import { GetFieldIsValidAndErrorMessage, DeleteField } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import usePasswordValidator, { PasswordValidatorProps } from "../../../Utils/Hooks/Validators/usePasswordValidator";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface FormInputPasswordProps extends Omit<OutlinedInputProps, "id" | "label" | "error" | "type"> {
  label?: string;
  register?: any;
  watch?: any;
  isPassword?: boolean;
  validationProps: PasswordValidatorProps;
  stackProps?: StackProps;
}

const FormInputPassword: FC<FormInputPasswordProps> = (props) => {
  const { label, register, watch, isPassword, validationProps, onChange, stackProps, ...otherProps } = props;
  const validatePassword = usePasswordValidator(validationProps);
  const dispatch = useAppDispatch();
  const { active: visibleRepeatPassword, toggle: toggleRepeatPasswordVisibility } = useToggle(false);
  const pwd = register ? watch("newPassword") : "";

  const errorMessage = useAppSelector(GetFieldIsValidAndErrorMessage(validationProps.formId, validationProps.fieldId));
  const isValid = !errorMessage;

  const [isValidMemo, errorMessageMemo] = useMemo(() => {
    return [isValid, errorMessage];
  }, [isValid, errorMessage]);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(event);
    validatePassword(event.target.value);
  };

  useEffect(() => {
    onChange && onChange(pwd);
    validatePassword(pwd);
  }, [pwd]);

  useEffect(() => {
    let value = typeof props.value === "string" ? props.value.toString() : "";
    validatePassword(value);
  }, [validationProps.required]);

  useEffect(() => {
    return () => {
      dispatch(
        DeleteField({
          formId: validationProps.formId,
          formSectionId: validationProps.formSectionId,
          fieldId: validationProps.fieldId,
        })
      );
    };
  }, []);

  return (
    <Stack {...stackProps}>
      {label && (
        <InputLabel sx={{ mb: 1 }} htmlFor={`${validationProps.fieldId}-label`} required={validationProps.required}>
          {label}
        </InputLabel>
      )}
      {register ? (
        <OutlinedInput
          type={visibleRepeatPassword ? "text" : "password"}
          {...otherProps}
          {...register("newPassword")}
          endAdornment={
            <IconButton sx={{ mr: -1 }} onClick={toggleRepeatPasswordVisibility}>
              {visibleRepeatPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          }
        />
      ) : (
        <OutlinedInput
          type={visibleRepeatPassword ? "text" : "password"}
          onChange={handleOnChange}
          {...otherProps}
          endAdornment={
            <IconButton sx={{ mr: -1 }} onClick={toggleRepeatPasswordVisibility}>
              {visibleRepeatPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          }
        />
      )}
      {!isValidMemo && <FormHelperText error>{errorMessageMemo}</FormHelperText>}
    </Stack>
  );
};

export default FormInputPassword;
